import React, { useState, useRef } from "react";
import supabase from "../services/supabaseConfig.js";
import { Link } from "react-router-dom";
import {
  determineDifficulty,
  parseText,
  separateListItems,
} from "../utils/resourcesUtils.js";

export default function Resources() {
  // Refs
  const lessonPlanRef = useRef(null);

  // Stateful Variables
  const [age, setAge] = useState(null);
  const [programmingLanguage, setProgrammingLanguage] = useState(null);
  const [studentInterests, setStudentInterests] = useState(null);
  const [studentName, setStudentName] = useState(null);

  const [generatedIdeas, setGeneratedIdeas] = useState([]);
  const [lessonPlan, setLessonPlan] = useState({
    description: null,
    tasks: null,
    notes: null,
  });

  const [id, setId] = useState(null);
  const [showLink, setShowLink] = useState(false);

  const [ideasButtonText, setIdeasButtonText] = useState("Generate Ideas");
  const [lessonButtonText, setLessonButtonText] = useState("Generate Lesson");

  // Content
  const generateIdeasMessages = [
    {
      role: "system",
      content:
        "Your job is to assist a coding tutor in generating a lesson plan for a student. In determining potential lesson plans, you will consider the students age, students interests and the programming language being used. You should provide ten total project plans; each project should have a brief description of the project, the end goal of the project, and the core concepts that would be covered. Make 5 of the projects related to the students' interests and make 5 of them unrelated to the students interests. Do not use numbers in number form. Thanks!",
    },
    {
      role: "user",
      content: `Hello. I am the coding tutor and am in need of your help to generate ten lesson plans for a student. The student is ${age} years old and the difficulty of the recommended projects should be ${determineDifficulty(
        age)}. The students interests are ${studentInterests}. Lastly, the programming language being used is ${programmingLanguage}. Thanks!`,
    },
    {
      role: "user",
      content: `
                  It is critical that your format of your response needs to be using "Description", "Tasks", and "Notes" in that order.
                  Example:
                  Project 1:
                  Description: Portfolio Website
                  Tasks: Create page, create header, add background,
                  Notes: The student is learning HTML and CSS.

                  Project 2:
                  Description: Hangman Game
                  Tasks: Create word list, create game loop, create win/lose conditions
                  Notes: The student is learning Python.
                `,
    },
    {
      role: "user",
      content: "Do not include any asterisks in your response.",
    },
  ];

  const generateLessonMessages = [
    {
      role: "system",
      content: `Your job is to generate a lesson plan for a student. 
                You will receive the project idea and will need to provide a detailed description of the project. 
                Additionally, you will need to provide ten tasks that the tutor will assign to the student. 
                Additionally, provide a note for the students parents about what was covered in the lesson, 
                which you can determine since you know the project idea and topics covered. 
                The lesson note should be casual and descriptive. 
                The description and tasks section will also be for the student so you must simplify your explanations so that a 5 year old child can understand it. 
                I can not stress this enough - simplify your description and tasks so a child can understand it. 
                Write it in first person too.
                Avoid writing tasks about installation, environments, and deployment. 
                Please separate your response into "Description", "Tasks", and "Notes" in that order. `,
    },
    {
      role: "user",
      content: `
                  It is critical that your format of your response needs to be using "Description", "Tasks", and "Notes" in that order.
                  Example:
                  Project 1:
                  Description: Portfolio Website
                  Tasks: Create page, create header, add background,
                  Notes: The student is learning HTML and CSS.

                  Project 2:
                  Description: Hangman Game
                  Tasks: Create word list, create game loop, create win/lose conditions
                  Notes: The student is learning Python.
                `,
    },
    {
      role: "user",
      content: `The student name is: ${studentName}. The project idea is: ${lessonPlan.description}. It is being made in ${programmingLanguage}.`,
    },
    {
      role: "user",
      content: "do not include any asterisks in your response.",
    },
  ];

  // Functions
  const generateIdeas = async () => {
    setIdeasButtonText("Generating...");
    fetch("https://api.openai.com/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
      },
      body: JSON.stringify({
        model: "gpt-3.5-turbo",
        messages: generateIdeasMessages,
      }),
    })
      .then((response) =>
        response.json().then((result) => {
          const response = result.choices[0].message.content;
          console.log("RESPONSE", response);
          const listItems = separateListItems(response);
          setGeneratedIdeas(listItems);
          setIdeasButtonText("Generate Ideas");
        })
      )
      .catch((error) => console.log("error", error));
  };

  const generateLesson = async () => {
    setLessonButtonText("Generating...");
    fetch("https://api.openai.com/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
      },
      body: JSON.stringify({
        model: "gpt-3.5-turbo",
        messages: generateLessonMessages ,
      }),
    })
      .then((response) => response.json())
      .then((result) => {
        const response = result.choices[0].message.content;
        const parsedResponse = parseText(response);
        setLessonPlan(parsedResponse);
        setLessonButtonText("Generate Lesson");
      })
      .catch((error) => console.log("error", error)); // TODO: Handle error
  };

  const createLink = async () => {
    const newId = Math.random().toString(36).substring(7);
    setId(newId);
    setShowLink(true);
    let result = { id: newId, showLink: false, error: null };

    try {
      const { data, error: selectError } = await supabase
        .from("lessons")
        .select();

      if (selectError) throw selectError;
      result.showLink = true;

      const { error: insertError } = await supabase.from("lessons").insert({
        id: newId,
        description: lessonPlan.description,
        tasks: lessonPlan.tasks,
        studentName: studentName,
      });

      if (insertError) throw insertError;
    } catch (error) {
      console.error("Error:", error.message);
      result.error = error.message;
    }

    return result;
  };

  return (
    <div className="flex flex-col lg:flex-row w-full justify-center lg:justify-around items-center">
      <div
        id="generateIdeasDiv"
        style={{ minHeight: "65vh", minWidth: "0vw", maxWidth: "40vw" }}
        className="mt-10 overflow-hidden bg-white shadow rounded-lg"
      >
        <div className="px-4 py-6 sm:px-6">
          <h3 className="text-base font-semibold leading-7 text-gray-900">
            Generate Lesson Ideas
          </h3>
          <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">
            Generates ten project ideas determined by age and language.
          </p>
        </div>
        <div className="border-t border-gray-100">
          <dl className="divide-y divide-gray-100">
            <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 items-center">
              <div>
                <dt className="text-sm font-medium text-gray-900">Age</dt>
                <dt className="text-xs font-small text-gray-800">
                  Determines complexity of project
                </dt>
              </div>
              <input
                className="pl-1 py-1 block w-full rounded-md border-0 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-400 sm:text-sm sm:leading-6"
                type="number"
                placeholder="8"
                onChange={(e) => setAge(e.target.value)}
              ></input>
            </div>

            <div className="px-4 py-6 sm:grid sm:grid-cols-3 items-center sm:gap-4 sm:px-6">
              <div>
                <dt className="text-sm font-medium text-gray-900">
                  Student Interests
                </dt>
                <dt className="text-xs font-small text-gray-800">
                  Helps find relevant project ideas
                </dt>
              </div>
              <textarea
                className="pl-1 py-1 block w-full rounded-md border-0 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6"
                type="text"
                placeholder="Hobbies, schoolwork, etc"
                onChange={(e) => setStudentInterests(e.target.value)}
              ></textarea>
            </div>

            <div className="px-4 py-6 sm:grid sm:grid-cols-3 items-center sm:gap-4 sm:px-6">
              <div>
                <dt className="text-sm font-medium text-gray-900">
                  Programming Language
                </dt>
                <dt className="text-xs font-small text-gray-800">
                  Scratch, Python, HTML
                </dt>
              </div>
              <input
                className="pl-1 py-1 block w-full rounded-md border-0 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6"
                type="text"
                placeholder="Python"
                onChange={(e) => setProgrammingLanguage(e.target.value)}
              ></input>
            </div>

            <div className="px-4 py-6">
              <button
                type="button"
                onClick={generateIdeas}
                className="rounded-full bg-white px-2.5 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                id="generate-ideas"
              >
                {ideasButtonText}
              </button>
            </div>
            {generatedIdeas.length > 0 && (
              <div className="px-4 py-6">
                <dt className="text-sm font-medium text-gray-900 px-2">
                  Ideas:
                </dt>
                {generatedIdeas.map((idea, index) => (
                  <div
                    className="flex flex-col justify-around items-start w-full"
                    key={index}
                  >
                    {idea.split(":").map((segment, i) => (
                      <p
                        className="px-2 py-1 text-sm font-light text-gray-900"
                        key={i}
                      >
                        {segment}
                      </p>
                    ))}
                    <button
                      className="bg-white mt-1 rounded-md px-2.5 ml-2 py-1 text-sm font-medium text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                      onClick={() => {
                        const newLessonPlan = {
                          ...lessonPlan,
                          description: idea,
                        };
                        setLessonPlan(newLessonPlan);
                        lessonPlanRef.current.scrollIntoView({
                          behavior: "smooth",
                        });
                      }}
                    >
                      Choose this idea
                    </button>
                    <br></br>
                    <br></br>
                  </div>
                ))}
              </div>
            )}
          </dl>
        </div>
      </div>

      <div
        id="generateLessonDiv"
        style={{ minHeight: "65vh", minWidth: "0vw", maxWidth: "40vw" }}
        className=" mt-10 overflow-hidden bg-white shadow rounded-lg"
      >
        <div ref={lessonPlanRef} className="px-4 py-6 sm:px-6">
          <h3 className="text-base font-semibold leading-7 text-gray-900">
            Generate Lesson Plan
          </h3>
          <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">
            Generate lesson description, tasks, and note.
          </p>
        </div>
        <div className="border-t border-gray-100">
          <dl className="divide-y divide-gray-100">
            <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 items-center">
              <div>
                <dt className="text-sm font-medium text-gray-900">
                  Student Name
                </dt>
                <dt className="text-xs font-small text-gray-800">
                  Used in lesson portal and notes
                </dt>
              </div>
              <input
                className="pl-1 py-1 block w-full rounded-md border-0 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-400 sm:text-sm sm:leading-6"
                type="text"
                placeholder="John Doe"
                onChange={(e) =>
                  setStudentName(e.target.value)
                }
              ></input>
            </div>
            <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 items-center">
              <div>
                <dt className="text-sm font-medium text-gray-900">
                  Project Idea
                </dt>
                <dt className="text-xs font-small text-gray-800">
                  Description, tasks, core concepts, and any other details
                </dt>
              </div>
              <textarea
                className="pl-1 py-1 block w-full rounded-md border-0 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-400 sm:text-sm sm:leading-6"
                type="number"
                placeholder="Create Portfolio Website"
                value={lessonPlan.description}
                onChange={(e) =>
                  setLessonPlan({ ...lessonPlan, description: e.target.value })
                }
              ></textarea>
            </div>
            <div className="px-4 py-6 sm:grid sm:grid-cols-3 items-center sm:gap-4 sm:px-6">
              <div>
                <dt className="text-sm font-medium text-gray-900">
                  Programming Language
                </dt>
                <dt className="text-xs font-small text-gray-800">
                  Scratch, Python, HTML
                </dt>
              </div>
              <input
                className="pl-1 py-1 block w-full rounded-md border-0 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6"
                type="text"
                placeholder="HTML"
                onChange={(e) => setProgrammingLanguage(e.target.value)}
              ></input>
            </div>
            <div className="px-4 py-6">
              <button
                type="button"
                onClick={generateLesson}
                className="rounded-full bg-white px-2.5 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                id="generate-lesson"
              >
                {lessonButtonText}
              </button>
            </div>
            {lessonPlan.description && lessonPlan.tasks && (
              <div className="px-4 py-6 flex flex-col justify-around items-center">
                <p className="px-2 py-4 text-sm font-light text-gray-900">
                  Description: {lessonPlan.description}
                </p>
                <br></br>
                <p className="px-2 py-4 text-sm font-light text-gray-900">
                  Tasks: {lessonPlan.tasks}
                </p>
                <br></br>
                <p className="px-2 py-4 text-sm font-light text-gray-900">
                  Notes: {lessonPlan.notes}
                </p>
                <button
                  className="rounded-full bg-white px-3.5 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                  onClick={createLink}
                >
                  Create Link
                </button>
                <Link to={`/lesson/${id}`}>
                  {showLink && (
                    <button
                      className="mt-2 rounded-full bg-white px-3.5 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                      onClick={createLink}
                    >
                      Join Link
                    </button>
                  )}
                </Link>
              </div>
            )}
          </dl>
        </div>
      </div>
    </div>
  );
}
